// Utility functions for preparing chart data from normalized analytics data

export const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  const numFloat = parseFloat(num);
  if (isNaN(numFloat)) return '0';
  return numFloat.toLocaleString();
};

export const formatPercentage = (num) => {
  if (num === null || num === undefined) return '0%';
  const numFloat = parseFloat(num);
  if (isNaN(numFloat)) return '0%';
  return `${numFloat.toFixed(1)}%`;
};

export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const getChangeIcon = (change) => {
  if (!change || change === 0) return 'minus';
  return change > 0 ? 'arrow-up' : 'arrow-down';
};

export const getChangeColor = (change) => {
  if (!change || change === 0) return 'neutral';
  return change > 0 ? 'positive' : 'negative';
};

// Helper function to calculate metrics from daily data
const calculateMetricsFromDailyData = (dailyData) => {
  if (!dailyData || dailyData.length === 0) return null;
  
  const totals = dailyData.reduce((acc, day) => {
    const dayReach = day.summary?.totalReach || day.insights?.totalReach || day.reach || 0;
    const dayEngagements = day.summary?.totalEngagements || day.insights?.totalEngagements || day.engagements || 0;
    const dayPosts = day.posts?.length || day.insights?.totalPosts || day.postCount || 0;
    const dayNewFollowers = day.summary?.totalNewFollowers || day.insights?.totalNewFollowers || day.newFollowers || 0;
    
    return {
      totalReach: acc.totalReach + dayReach,
      totalEngagements: acc.totalEngagements + dayEngagements,
      totalPosts: acc.totalPosts + dayPosts,
      totalNewFollowers: acc.totalNewFollowers + dayNewFollowers,
      days: acc.days + 1
    };
  }, { totalReach: 0, totalEngagements: 0, totalPosts: 0, totalNewFollowers: 0, days: 0 });
  
  return {
    totalReach: totals.totalReach,
    totalEngagements: totals.totalEngagements,
    totalPosts: totals.totalPosts,
    totalNewFollowers: totals.totalNewFollowers,
    avgDailyReach: totals.days > 0 ? totals.totalReach / totals.days : 0,
    engagementRate: totals.totalReach > 0 ? (totals.totalEngagements / totals.totalReach) * 100 : 0
  };
};

// Chart data preparation functions
export const prepareEngagementTrendData = (data) => {
  if (!data?.dailyData?.length) {
    return null; // Return null instead of mock data
  }

  return data.dailyData.slice(-14).map(day => ({
    date: formatDate(day.date),
    engagement: day.summary?.totalEngagements || day.insights?.totalEngagements || day.engagements || 0,
    reach: day.summary?.totalReach || day.insights?.totalReach || day.reach || 0,
    posts: day.posts?.length || day.insights?.totalPosts || day.postCount || 0,
    comments: day.comments?.length || day.insights?.totalComments || day.commentCount || 0
  }));
};

export const prepareGrowthData = (data) => {
  if (!data?.dailyData?.length) {
    return null; // Return null instead of mock data
  }

  return data.dailyData.slice(-14).map(day => ({
    date: formatDate(day.date),
    newFollowers: day.summary?.totalNewFollowers || day.insights?.totalNewFollowers || day.newFollowers || 0,
    unfollows: Math.abs(day.summary?.totalUnfollows || day.insights?.totalUnfollows || day.unfollows || 0),
    netGrowth: (day.summary?.totalNewFollowers || day.insights?.totalNewFollowers || day.newFollowers || 0)
              - (Math.abs(day.summary?.totalUnfollows || day.insights?.totalUnfollows || day.unfollows || 0))
  }));
};

export const prepareContentPerformanceData = (data) => {
  const contentPerf = data?.analysis?.contentPerformance;
  
  if (!contentPerf?.contentTypes || Object.keys(contentPerf.contentTypes).length === 0) {
    return null; // Return null instead of mock data
  }

  return Object.entries(contentPerf.contentTypes).map(([type, stats]) => ({
    type: type.charAt(0).toUpperCase() + type.slice(1),
    posts: stats.count || 0,
    avgEngagement: stats.avgEngagement || 0,
    engagementRate: stats.engagementRate || 0
  }));
};

export const prepareQualityScoreData = (data) => {
  const contentPerf = data?.analysis?.contentPerformance;
  
  if (!contentPerf?.qualityAnalysis) {
    return null; // Return null instead of mock data
  }

  const quality = contentPerf.qualityAnalysis;
  return [
    { name: 'High Quality', value: quality.highQualityPosts || 0, color: '#10b981' },
    { name: 'Medium Quality', value: quality.mediumQualityPosts || 0, color: '#f59e0b' },
    { name: 'Low Quality', value: quality.lowQualityPosts || 0, color: '#ef4444' }
  ].filter(item => item.value > 0);
};

export const prepareAudienceMetricsData = (data) => {
  const audienceMetrics = data?.analysis?.audienceMetrics;
  
  // Try to get metrics from audienceMetrics first, then fall back to quality factors
  const growthMetrics = audienceMetrics?.growthMetrics || {};
  const engagementMetrics = audienceMetrics?.engagementMetrics || {};
  const activityMetrics = audienceMetrics?.activityMetrics || {};
  const qualityFactors = audienceMetrics?.qualityMetrics?.qualityFactors || {};
  
  // Use quality factors as fallback if main metrics are not available
  const churnRate = growthMetrics.churnRate || qualityFactors.churnRate || 0;
  const retentionRate = growthMetrics.retentionRate || qualityFactors.retentionRate || 0;
  const activityRate = activityMetrics.audienceActivityRate || qualityFactors.activityRate || 0;
  const loyaltyRate = activityMetrics.loyaltyRate || qualityFactors.loyaltyRate || 0;
  const engagementRate = engagementMetrics.avgEngagementRate || qualityFactors.engagementRate || 0;
  
  return [
    {
      metric: 'Churn Rate',
      value: churnRate,
      color: '#ef4444'
    },
    {
      metric: 'Retention Rate',
      value: retentionRate,
      color: '#10b981'
    },
    {
      metric: 'Activity Rate',
      value: activityRate,
      color: '#6366f1'
    },
    {
      metric: 'Loyalty Rate',
      value: loyaltyRate,
      color: '#8b5cf6'
    },
    {
      metric: 'Engagement Rate',
      value: engagementRate,
      color: '#f59e0b'
    }
  ];
};

export const prepareAudienceMetricsTrendsData = (data) => {
  if (!data?.dailyData?.length) return null;

  return data.dailyData.map(day => {
    // Only use metrics that are actually available in the API data
    const dayReach = day.summary?.totalReach || day.insights?.totalReach || day.reach || 0;
    const dayEngagements = day.summary?.totalEngagements || day.insights?.totalEngagements || day.engagements || 0;
    const dayNewFollowers = day.summary?.totalNewFollowers || day.insights?.totalNewFollowers || day.newFollowers || 0;
    const dayUnfollows = day.summary?.totalUnfollows || day.insights?.totalUnfollows || day.unfollows || 0;
    
    // Calculate engagement rate from available data
    const calculatedEngagementRate = dayReach > 0 ? (dayEngagements / dayReach) * 100 : 0;
    
    // Calculate retention rate (simplified: new followers / (new followers + unfollows))
    const totalFollowers = dayNewFollowers + Math.abs(dayUnfollows);
    const calculatedRetentionRate = totalFollowers > 0 ? (dayNewFollowers / totalFollowers) * 100 : 0;
    
    // Calculate churn rate (unfollows / total followers)
    const calculatedChurnRate = totalFollowers > 0 ? (Math.abs(dayUnfollows) / totalFollowers) * 100 : 0;
    
    // For activity rate, we'll use engagement rate as a proxy since we don't have direct activity data
    const calculatedActivityRate = calculatedEngagementRate;
    
    // For loyalty rate, we'll use a combination of retention and engagement
    const calculatedLoyaltyRate = (calculatedRetentionRate + calculatedEngagementRate) / 2;
    
    return {
      date: formatDate(day.date),
      churnRate: calculatedChurnRate,
      retentionRate: calculatedRetentionRate,
      activityRate: calculatedActivityRate,
      loyaltyRate: calculatedLoyaltyRate,
      engagementRate: calculatedEngagementRate
    };
  });
};

// New function to calculate summary metrics from available data
export const calculateSummaryMetrics = (data) => {
  if (!data) return null;
  
  // First try to use existing summary data
  if (data.summary && (data.summary.totalReach > 0 || data.summary.totalEngagements > 0)) {
    return data.summary;
  }
  
  // Calculate from daily data if summary is missing or has zeros
  const calculatedMetrics = calculateMetricsFromDailyData(data.dailyData);
  if (calculatedMetrics) {
    return {
      totalPosts: calculatedMetrics.totalPosts,
      totalReach: calculatedMetrics.totalReach,
      totalEngagements: calculatedMetrics.totalEngagements,
      engagementRate: calculatedMetrics.engagementRate,
      totalDays: data.period?.days || 0,
      totalWeeks: Math.ceil((data.period?.days || 0) / 7)
    };
  }
  
  // Fallback to original summary
  return data.summary || {
    totalPosts: 0,
    totalReach: 0,
    totalEngagements: 0,
    engagementRate: 0,
    totalDays: 0,
    totalWeeks: 0
  };
}; 